/* global ScrollyVideo */

if (!customElements.get('video-scroll-effect')) {
  /**
   * Scroll-driven video scrubbing.
   *
   * Rendering is delegated to ScrollyVideo (https://github.com/dkaoster/scrolly-video),
   * which decodes the mp4 into individual frames via the WebCodecs API and
   * paints them to a canvas. That bypasses video.currentTime seeking entirely,
   * so backward scrubbing is exactly as smooth as forward. Browsers without
   * WebCodecs gracefully fall back to the video element.
   *
   * This element owns the scroll math: it sizes itself based on the video
   * duration and the scroll-speed setting, then maps the scroll progress
   * through the section to a video percentage.
   */
  class VideoScrollEffect extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('.video-scroll-effect__stage');
      this.src = this.dataset.src;
      if (!this.stage || !this.src) return;

      this.scrollSpeed = Number.parseFloat(this.dataset.scrollSpeed) || 100;
      this.initialized = false;
      this.isActive = false;
      this.rafId = null;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onIntersect = this.onIntersect.bind(this);

      // On mobile the scroll effect is disabled — the video just plays
      // normally. Also avoids decoding the full frame cache on devices
      // with limited memory.
      if (window.matchMedia('(max-width: 767px)').matches) {
        this.renderFallbackVideo('autoplay');
        return;
      }

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.renderFallbackVideo('controls');
        return;
      }

      if (typeof ScrollyVideo === 'undefined') {
        this.renderFallbackVideo('controls');
        return;
      }

      // cover: false — fit is handled entirely in CSS via object-fit, which
      // behaves predictably for both the <video> and the WebCodecs <canvas>.
      this.scrolly = new ScrollyVideo({
        src: this.src,
        scrollyVideoContainer: this.stage,
        cover: false,
        sticky: true,
        full: true,
        trackScroll: false,
      });

      const video = this.scrolly.video;
      const init = () => this.init();
      if (video.readyState >= 1) {
        init();
      } else {
        video.addEventListener('loadedmetadata', init, { once: true });
      }
    }

    disconnectedCallback() {
      this.stopLoop();
      this.observer?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.scrolly?.destroy();
      this.scrolly = null;
    }

    /**
     * Plain video without the scroll effect.
     * @param {string} mode - 'autoplay' (mobile: plays normally, muted+looped)
     *   or 'controls' (reduced motion / no library).
     */
    renderFallbackVideo(mode) {
      this.dataset.fallback = 'true';
      this.style.height = 'auto';

      const video = document.createElement('video');
      video.className = 'video-scroll-effect__video';
      video.src = this.src;
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');

      if (mode === 'autoplay') {
        video.autoplay = true;
        video.loop = true;
        video.preload = 'auto';
      } else {
        video.controls = true;
        video.preload = 'metadata';
      }

      this.stage.appendChild(video);

      // Play only while on screen (saves battery, required for autoplay
      // to restart reliably on some mobile browsers).
      if (mode === 'autoplay' && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) {
            const playPromise = video.play();
            if (playPromise) playPromise.catch(() => {});
          } else {
            video.pause();
          }
        }, { rootMargin: '0px 0px 200px 0px' });
        this.observer.observe(this);
      }
    }

    init() {
      if (this.initialized) return;

      this.duration = this.scrolly.video.duration;
      if (!this.duration || !Number.isFinite(this.duration)) return;

      this.initialized = true;
      this.setScrollHeight();

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });

      this.observer = new IntersectionObserver(this.onIntersect, {
        rootMargin: '20% 0px',
        threshold: 0,
      });
      this.observer.observe(this);

      this.update(true);
    }

    setScrollHeight() {
      // One viewport of scroll per second of video at 100% speed; higher
      // speed shortens the distance so the video advances faster.
      const speedFactor = this.scrollSpeed / 100;
      this.scrollDistance = (this.duration * window.innerHeight) / speedFactor;
      this.style.height = `calc(100vh + ${this.scrollDistance}px)`;
    }

    onIntersect([entry]) {
      this.isActive = entry.isIntersecting;
      if (!this.isActive) this.stopLoop();
    }

    onScroll() {
      if (!this.initialized || !this.isActive) return;
      this.startLoop();
    }

    onResize() {
      if (!this.initialized) return;
      this.setScrollHeight();
      this.update(true);
    }

    /**
     * rAF loop while scrolling: feeds the latest progress to ScrollyVideo,
     * which transitions to it smoothly (canvas frames in both directions).
     */
    startLoop() {
      if (this.rafId !== null) return;
      const tick = () => {
        this.update();
        this.rafId = this.isActive ? requestAnimationFrame(tick) : null;
      };
      this.rafId = requestAnimationFrame(tick);
    }

    stopLoop() {
      if (this.rafId === null) return;
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    update(jump = false) {
      const scrolled = -this.getBoundingClientRect().top;
      const progress = Math.min(1, Math.max(0, scrolled / this.scrollDistance));
      this.scrolly.setVideoPercentage(progress, jump ? { jump: true } : undefined);
    }
  }

  customElements.define('video-scroll-effect', VideoScrollEffect);
}
